import { StyledPlayFeaturedIcon } from '../../../icons/PlayFeaturedIcon'
import { ArticleProgressBar } from './ArticleProgressBar'
import { ArticleActionsContainer } from '../styled/ArticleActionsContainer.styled'
import { ArticlePlayingContainer } from '../styled/ArticlePlayingContainer.styled'
import { StyledSpacer } from '../styled/StyledSpacer.styled'
import { ArticleMetadataParagraph } from '../styled/ArticleMetadataParagraph.styled'
import { ArticleProgressBarAndTimeContainer } from '../styled/ArticleProgressBarAndTimeContainer.styled'
import { StyledFeaturedArticleTitle } from '../styled/ArticleTitle.styled'
import { PlayIconAndProgressBarContainer } from '../styled/PlayIconAndProgressBarContainer.styled'
import { IArticleTransformed } from '../types/IArticleTransformed'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
    playOrPauseArticle,
    selectAudioState,
    selectCurrentlyPlayingArticle,
    seek15SecsBack,
    copyToClipboardToastThunk,
    selectAudioSpeed,
} from '../data/articleSlice'
import { ArticleTime } from './ArticleTime'
import { StyledPauseFeaturedIcon } from '../../../icons/PauseFeaturedIcon'
import { selectTheme } from '../../theme/data/themeSlice'
import { ArticleNavigationContainer } from '../styled/ArticleNavigationContainer.styled'
import { StyledArticlesIcon } from '../../../icons/ArticlesIcon'
import { ArticleNavigation } from '../styled/ArticleNavigation'
import { ArticleNavigationItem } from '../styled/ArticleNavigationItem'
import { StyledSearchIcon } from '../../../icons/SearchIcon'
import { StyledQueueIcon } from '../../../icons/QueueIcon'
import { ArticleActionButtons } from '../styled/ArticleActionButtons'
import { FifteenSecBackButton } from '../styled/FifteenSecBackButton.styled'
import { SpeedAdjustmentButton } from '../styled/SpeedAdjustmentButton.styled'
import { EllipsesMenuButton } from '../styled/EllipsesMenuButton.styled'
import { StyledFifteenSecBackIcon } from '../../../icons/FifteenSecBackIcon'
import { StyledEllipsesIcon } from '../../../icons/EllipsesIcon'
import { IPages } from '../../../shared/types/IPages'
import {
    selectActivePage,
    selectIsDateEnabled,
    selectIsQueueEnabled,
    selectIsSearchEnabled,
    setActivePage,
} from '../../application/applicationSlice'
import Popup from '../../../shared/components/Popup'
import { EllipsisActionButtons } from '../styled/EllipsisActionButtons'
import { StyledShareIcon } from '../../../icons/ShareIcon'
import { StyledArticleTextIcon } from '../../../icons/ArticleTextIcon'
import { StyledSpeedIcon } from '../../../icons/SpeedIcon'
import { StyledChatIcon } from '../../../icons/ChatIcon'
import SpeedAdjustmentPopup from './SpeedAdjustmentPopup'
import { StyledTooltip } from '../../../shared/styled/StyledTooltip'
import VolumeSlider from './VolumeSlider'
import { StyledVolumeIcon } from '../../../icons/VolumeIcon'
import { SpeedAdjustmentButtonWrapper } from '../styled/SpeedAdjustmentButtonWrapper'
import { PopupSpeedAdjustmentButtonWrapper } from '../styled/PopupSpeedAdjustmentButtonWrapper'
import { isDesktop } from 'react-device-detect'
import { mpTrackButtonClick } from '../../../tracking/mixpanel'
import MpTrackingButtons from '../../../tracking/types/mpTrackingButtons'
import { PopupSpeedAdjustmentButton } from '../styled/PopupSpeedAdjustmentButton'
interface IProps {
    article: IArticleTransformed
}

export const PlaylistWidgetPlayer = (props: IProps) => {
    const dispatch = useAppDispatch()

    const audioState = useAppSelector(selectAudioState)
    const currentlyPlayingArticle = useAppSelector(selectCurrentlyPlayingArticle)
    const theme = useAppSelector(selectTheme)
    const activePage = useAppSelector(selectActivePage)
    const isSearchEnabled = useAppSelector(selectIsSearchEnabled)
    const isQueueEnabled = useAppSelector(selectIsQueueEnabled)
    const isDateEnabled = useAppSelector(selectIsDateEnabled)
    const audioSpeed = useAppSelector(selectAudioSpeed)

    const onClickPlayPause = () => dispatch(playOrPauseArticle())
    const onPageUpdate = (page: IPages) => () => {
        const mpTrackingButton =
            page === IPages.ARTICLES
                ? MpTrackingButtons.TRENDING_ARTICLES_PAGE
                : page === IPages.QUEUE
                ? MpTrackingButtons.QUEUE_PAGE
                : MpTrackingButtons.SEARCH_PAGE

        mpTrackButtonClick(mpTrackingButton, currentlyPlayingArticle)
        dispatch(setActivePage(page))
    }

    const showChat = () => {
        window.Intercom('show')
    }

    const openArticleText = () => {
        if (currentlyPlayingArticle) {
            mpTrackButtonClick(MpTrackingButtons.ARTICLE_TEXT, currentlyPlayingArticle)
            window.open(currentlyPlayingArticle.articleOriginUrl, '_blank')
        }
    }

    const showPauseIcon =
        currentlyPlayingArticle != null && currentlyPlayingArticle.id === props.article.id && audioState === 'playing'

    return (
        <ArticlePlayingContainer {...theme}>
            <div>
                <ArticleMetadataParagraph color={theme.playerDateAndLengthColor}>
                    {isDateEnabled ? `${props.article.date} • ` : ''}
                    {props.article.audioLength.text}
                </ArticleMetadataParagraph>
                <StyledFeaturedArticleTitle>
                    {props.article.name}
                    {props.article.queueId ? <span style={{ color: 'red' }}> (from queue)</span> : ''}
                </StyledFeaturedArticleTitle>
            </div>

            <ArticleActionsContainer>
                <PlayIconAndProgressBarContainer>
                    {showPauseIcon ? (
                        <StyledPauseFeaturedIcon onClick={onClickPlayPause} />
                    ) : (
                        <StyledPlayFeaturedIcon onClick={onClickPlayPause} />
                    )}
                    <ArticleProgressBarAndTimeContainer>
                        {/* Purely added to ensure progress bar is centered */}
                        <StyledSpacer>Spacer</StyledSpacer>
                        <ArticleProgressBar />
                        <ArticleTime />
                    </ArticleProgressBarAndTimeContainer>
                </PlayIconAndProgressBarContainer>
            </ArticleActionsContainer>

            <ArticleNavigationContainer>
                {(isSearchEnabled || isQueueEnabled) && (
                    <ArticleNavigation>
                        <ArticleNavigationItem
                            active={activePage === IPages.ARTICLES}
                            onClick={onPageUpdate(IPages.ARTICLES)}>
                            <StyledArticlesIcon active={activePage === IPages.ARTICLES} />
                        </ArticleNavigationItem>
                        {isSearchEnabled && (
                            <ArticleNavigationItem
                                active={activePage === IPages.SEARCH}
                                onClick={onPageUpdate(IPages.SEARCH)}>
                                <StyledSearchIcon active={activePage === IPages.SEARCH} />
                            </ArticleNavigationItem>
                        )}
                        {isQueueEnabled && (
                            <ArticleNavigationItem
                                active={activePage === IPages.QUEUE}
                                onClick={onPageUpdate(IPages.QUEUE)}>
                                <StyledQueueIcon active={activePage === IPages.QUEUE} />
                            </ArticleNavigationItem>
                        )}
                    </ArticleNavigation>
                )}

                <ArticleActionButtons style={{ marginLeft: 'auto' }}>
                    <FifteenSecBackButton onClick={() => dispatch(seek15SecsBack())}>
                        <StyledFifteenSecBackIcon />
                        <p>15</p>
                    </FifteenSecBackButton>

                    <SpeedAdjustmentButtonWrapper alwaysOpen={false}>
                        <SpeedAdjustmentPopup position={'right'}>
                            <SpeedAdjustmentButton>
                                {audioSpeed}
                                <span>x</span>
                            </SpeedAdjustmentButton>
                        </SpeedAdjustmentPopup>
                    </SpeedAdjustmentButtonWrapper>

                    <Popup
                        content={
                            <EllipsisActionButtons>
                                <StyledTooltip text='Chat with us'>
                                    <button style={{ marginLeft: 0 }} popup-depth='1' onClick={showChat}>
                                        <StyledChatIcon
                                            fill={theme.popupForegroundColor}
                                            stroke={theme.popupForegroundColor}
                                        />
                                    </button>
                                </StyledTooltip>
                                <StyledTooltip text='Open article text'>
                                    <button popup-depth='1' onClick={openArticleText}>
                                        <StyledArticleTextIcon
                                            fill={theme.popupForegroundColor}
                                            stroke={theme.popupForegroundColor}
                                        />
                                    </button>
                                </StyledTooltip>
                                <Popup
                                    closeOnClick={false}
                                    isNested
                                    content={
                                        <p
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '21px',
                                                width: 'max-content',
                                                color: theme.popupForegroundColor,
                                            }}>
                                            Article link copied to your clipboard.
                                        </p>
                                    }>
                                    <button onClick={() => dispatch(copyToClipboardToastThunk())} popup-depth='1'>
                                        <StyledShareIcon fill={theme.popupForegroundColor} />
                                    </button>
                                </Popup>
                                {isDesktop && (
                                    <Popup
                                        isNested
                                        content={
                                            <div style={{ width: '160px', padding: '2px' }}>
                                                <VolumeSlider
                                                    foregroundColor={theme.popupActiveForegroundColor}
                                                    backgroundColor={theme.popupForegroundColor}
                                                />
                                            </div>
                                        }>
                                        <button popup-depth='1'>
                                            <StyledVolumeIcon
                                                fill={theme.popupForegroundColor}
                                                stroke={theme.popupForegroundColor}
                                            />
                                        </button>
                                    </Popup>
                                )}
                                <PopupSpeedAdjustmentButtonWrapper>
                                    <SpeedAdjustmentPopup isNested position='right'>
                                        <button popup-depth='1'>
                                            <PopupSpeedAdjustmentButton>
                                                {audioSpeed}
                                                <span>x</span>
                                            </PopupSpeedAdjustmentButton>
                                        </button>
                                    </SpeedAdjustmentPopup>
                                </PopupSpeedAdjustmentButtonWrapper>
                            </EllipsisActionButtons>
                        }
                        popupStyles={{ padding: '8px 12px' }}>
                        <EllipsesMenuButton>
                            <StyledEllipsesIcon />
                        </EllipsesMenuButton>
                    </Popup>
                </ArticleActionButtons>
            </ArticleNavigationContainer>
        </ArticlePlayingContainer>
    )
}
